import { Goal, ID } from '@griply/types';

import { getGoalAncestors } from './get-goal-ancestors';

export const isGoalArchived = (goal: Goal, goals: Goal[]): boolean => {
  if (goal.archivedAt) {
    return true;
  }
  return getGoalAncestors(goal, goals).some(
    (ancestor) => !!ancestor.archivedAt,
  );
};

const collectGoalIdsWithInheritedFlag = (
  goals: Goal[],
  hasFlag: (goal: Goal) => boolean,
): Set<ID> => {
  const goalsMap = new Map(goals.map((goal) => [goal.id, goal]));
  const resolved = new Map<ID, boolean>();

  const resolve = (goal: Goal, visited: Set<ID>): boolean => {
    const cached = resolved.get(goal.id);
    if (cached !== undefined) {
      return cached;
    }
    if (hasFlag(goal)) {
      resolved.set(goal.id, true);
      return true;
    }
    const parentId = goal.parentIds?.[goal.parentIds.length - 1];
    const parent = parentId ? goalsMap.get(parentId) : undefined;
    if (!parent || visited.has(parent.id)) {
      resolved.set(goal.id, false);
      return false;
    }
    visited.add(parent.id);
    const result = resolve(parent, visited);
    resolved.set(goal.id, result);
    return result;
  };

  const flaggedIds = new Set<ID>();
  goals.forEach((goal) => {
    if (resolve(goal, new Set<ID>([goal.id]))) {
      flaggedIds.add(goal.id);
    }
  });
  return flaggedIds;
};

export const getArchivedGoalIds = (goals: Goal[]): Set<ID> =>
  collectGoalIdsWithInheritedFlag(goals, (goal) => !!goal.archivedAt);

export const getCompletedGoalIds = (goals: Goal[]): Set<ID> =>
  collectGoalIdsWithInheritedFlag(goals, (goal) => !!goal.completedAt);
